import React, {
    useEffect,
    useRef,
    useState,
    memo,
    createContext,
    useContext,
    useCallback
} from 'react'
import {Portal} from '@chakra-ui/react'
import PropTypes from 'prop-types'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'

export const GOOGLE_MAPS_API_URL = 'https://maps.googleapis.com/maps/api/js'
export const GOOGLE_MAPS_API_ID = 'google-maps-script-id'

import useMultiSite from '@app/hooks/use-multi-site'
import useShipToCountry from '@app/hooks/use-ship-to-country'

export const googleMapsApiStatus = {
    isLoaded: false,
    isLoading: false,
    error: null
}

const onClient = typeof window !== 'undefined'

const buildGoogleMapsApiUrl = ({apiKey, language, region, libraries = [], version, callback}) => {
    if (!apiKey) {
        throw new Error('API key is required to build the Google Maps API URL.')
    }

    const urlParams = new URLSearchParams({
        key: apiKey,
        loading: 'async'
    })

    if (language) urlParams.set('language', language)
    if (region) urlParams.set('region', region)
    if (version) urlParams.set('v', version)
    if (callback) urlParams.set('callback', callback)
    if (libraries.length > 0) urlParams.set('libraries', libraries.join(','))

    return `${GOOGLE_MAPS_API_URL}?${urlParams.toString()}`
}

/**
 * Custom React hook to dynamically load the Google Maps JavaScript API with configurable options.
 *
 * This hook manages the loading state and error handling for the Google Maps API script,
 * allowing consumers to specify language, region, libraries, and whether to force a reload.
 * It uses application configuration and site context to determine sensible defaults for language and region.
 *
 * @param {Object} [options={}] - Configuration options for loading the Google Maps API.
 * @param {string} [options.language] - Custom language code for the API (defaults to current site locale).
 * @param {string} [options.region] - Custom region code for the API (defaults to current shipping country).
 * @param {Array<string>} [options.libraries] - Array of Google Maps libraries to load (e.g., ['places']).
 *
 * @returns {Object} An object containing:
 *   - {boolean} isApiLoaded: Indicates whether the Google Maps API script has finished loading.
 *   - {Error|null} error: Any error encountered during script loading, or null if none.
 */
export const useGoogleMapsApi = ({
    language: customLanguage,
    region: customRegion,
    libraries
} = {}) => {
    const {app: appConfig} = getConfig()
    const GOOGLE_MAPS_API_KEY = appConfig.GOOGLE_API_KEY
    const GOOGLE_MAPS_VERSION = appConfig.GOOGLE_MAPS_VERSION

    // Get default language and region
    const {locale} = useMultiSite()
    const defaultLanguage = locale.alias
    const {shipToCountry} = useShipToCountry()
    const defaultRegion = shipToCountry?.id || ''

    // Use provided params or defaults
    const language = customLanguage || defaultLanguage
    const region = customRegion || defaultRegion
    const librariesToLoad = libraries || []

    const [isApiLoaded, setIsApiLoaded] = useState(googleMapsApiStatus.isLoaded)
    const [isLibrariesLoaded, setIsLibrariesLoaded] = useState(false)
    const [isApiAndLibrariesLoaded, setIsApiAndLibrariesLoaded] = useState(false)
    const [error, setError] = useState(googleMapsApiStatus.error)

    const loadGoogleMapsApi = () => {
        return new Promise((resolve, reject) => {
            if (googleMapsApiStatus.isLoaded || googleMapsApiStatus.isLoading) {
                return resolve()
            }

            const existingScript = document.getElementById(GOOGLE_MAPS_API_ID)
            if (!existingScript) {
                googleMapsApiStatus.isLoading = true

                window.__googleMapsCallback__ = () => {
                    googleMapsApiStatus.isLoaded = true
                    googleMapsApiStatus.isLoading = false
                    setIsApiLoaded(true)
                    setIsLibrariesLoaded(true)
                    resolve()
                }

                window.gm_authFailure = () => {
                    const error = new Error('Google Maps API authentication failed.')
                    googleMapsApiStatus.error = error
                    googleMapsApiStatus.isLoading = false
                    setError(error)
                    reject(error)
                }

                try {
                    const script = document.createElement('script')
                    script.id = GOOGLE_MAPS_API_ID
                    script.src = buildGoogleMapsApiUrl({
                        apiKey: GOOGLE_MAPS_API_KEY,
                        language,
                        region,
                        libraries: librariesToLoad,
                        version: GOOGLE_MAPS_VERSION,
                        callback: '__googleMapsCallback__'
                    })
                    script.async = true
                    script.onerror = (err) => {
                        googleMapsApiStatus.error = err
                        googleMapsApiStatus.isLoading = false
                        setError(err)
                        reject(err)
                    }

                    document.head.appendChild(script)
                } catch (err) {
                    googleMapsApiStatus.error = err
                    googleMapsApiStatus.isLoading = false
                    setError(err)
                    reject(err)
                }
            } else {
                resolve()
            }
        })
    }

    useEffect(() => {
        setIsApiAndLibrariesLoaded(isApiLoaded && isLibrariesLoaded)
    }, [isApiLoaded, isLibrariesLoaded])

    useEffect(() => {
        const initGoogleMapsApiLoad = async () => {
            try {
                // ensure all libraries are loaded
                if (googleMapsApiStatus.isLoaded) {
                    await importGoogleMapsLibraries(libraries)
                    setIsLibrariesLoaded(true)
                } else {
                    await loadGoogleMapsApi()
                }
            } catch (error) {
                console.error('Error initializing Google Maps:', error)
            }
        }
        initGoogleMapsApiLoad()

        return () => {
            const script = document.getElementById(GOOGLE_MAPS_API_ID)
            if (script) {
                script.remove()
            }
        }
    }, [])

    return {isApiLoaded, isApiAndLibrariesLoaded, error}
}

/**
 * Asynchronously imports specified Google Maps JavaScript API libraries if they are not already loaded.
 *
 * @param {string[]} [libraries=[]] - An array of Google Maps library names to import (e.g., ['places', 'geometry']).
 * @throws {Error} Throws an error if the Google Maps API is not loaded or if a library fails to load.
 * @returns {Promise<void>} Resolves when all specified libraries are successfully loaded.
 *
 * @example
 * await importGoogleMapsLibraries(['places', 'geometry']);
 */
export const importGoogleMapsLibraries = async (libraries = []) => {
    if (!isGoogleMapsApiLoaded()) {
        throw new Error('Google Maps API is not loaded.')
    }

    for (const library of libraries) {
        if (!isGoogleMapsLibraryLoaded(library)) {
            try {
                await window.google.maps.importLibrary(library)
            } catch (error) {
                console.error(`Failed to load Google Maps library: ${library}`, error)
                throw error
            }
        }
    }
}

export const isGoogleMapsApiLoaded = () => {
    return onClient && window.google && window.google.maps && googleMapsApiStatus.isLoaded
}

export const isGoogleMapsLibraryLoaded = (library) => {
    return isGoogleMapsApiLoaded() && !!window.google.maps[library]
}

export const GoogleMapsContext = createContext(null)

/**
 * GoogleMaps is a React component that integrates the Google Maps.
 * It provides a flexible and extensible map container that supports a wide range of map options, event handlers,
 * and custom child components via context.
 *
 * @param {Object} props - The properties object.
 * @param {string} props.id - The unique identifier for the map container and Google Maps mapId.
 * @param {boolean} [props.isApiLoaded=false] - Indicates if the Google Maps API has been loaded.
 * @param {React.ReactNode} props.children - Child components to render within the map context.
 * ...
 *
 * @returns {JSX.Element} The rendered Google Maps container with context provider for map and marker state.
 */
const GoogleMaps = ({
    id, // map ID is required for advanced markers rendering
    isApiLoaded = false,
    children,
    mapContainerStyle,
    mapContainerClassName,
    options,
    center,
    zoom,
    onLoad,
    onUnmount,
    onClick,
    onDblClick,
    onDrag,
    onDragEnd,
    onDragStart,
    onMouseMove,
    onMouseOut,
    onMouseOver,
    onMouseDown,
    onMouseUp,
    onRightClick,
    onCenterChanged
}) => {
    const mapRef = useRef(null)
    const [map, setMap] = useState(null)
    const [activeMarker, setActiveMarker] = useState(null)

    useEffect(() => {
        if (isApiLoaded && mapRef.current && !map) {
            if (isGoogleMapsApiLoaded()) {
                const mapInstance = new window.google.maps.Map(mapRef.current, {
                    ...options,
                    ...(center && {center}),
                    zoom,
                    mapId: id // Map ID is required for advanced markers rendering
                })
                setMap(mapInstance)

                if (onLoad) {
                    onLoad(mapInstance)
                }
            }
        }

        return () => {
            if (map && onUnmount) {
                onUnmount(map)
            }
        }
    }, [isApiLoaded, mapRef])

    useEffect(() => {
        if (map && center) {
            map.setCenter(center)
        }
    }, [map, center])

    useEffect(() => {
        if (map) {
            map.setZoom(zoom)
        }
    }, [map, zoom])

    useEffect(() => {
        if (map) {
            const listeners = []

            if (onClick) listeners.push(map.addListener('click', onClick))
            if (onDblClick) listeners.push(map.addListener('dblclick', onDblClick))
            if (onDrag) listeners.push(map.addListener('drag', onDrag))
            if (onDragEnd) listeners.push(map.addListener('dragend', onDragEnd))
            if (onDragStart) listeners.push(map.addListener('dragstart', onDragStart))
            if (onMouseMove) listeners.push(map.addListener('mousemove', onMouseMove))
            if (onMouseOut) listeners.push(map.addListener('mouseout', onMouseOut))
            if (onMouseOver) listeners.push(map.addListener('mouseover', onMouseOver))
            if (onMouseDown) listeners.push(map.addListener('mousedown', onMouseDown))
            if (onMouseUp) listeners.push(map.addListener('mouseup', onMouseUp))
            if (onRightClick) listeners.push(map.addListener('rightclick', onRightClick))
            if (onCenterChanged) listeners.push(map.addListener('center_changed', onCenterChanged))

            return () => {
                listeners.forEach((listener) => listener.remove())
            }
        }
    }, [
        map,
        onClick,
        onDblClick,
        onDrag,
        onDragEnd,
        onDragStart,
        onMouseMove,
        onMouseOut,
        onMouseOver,
        onMouseDown,
        onMouseUp,
        onRightClick,
        onCenterChanged
    ])

    return (
        <div id={id} ref={mapRef} style={mapContainerStyle} className={mapContainerClassName}>
            <GoogleMapsContext.Provider value={{map, activeMarker, setActiveMarker}}>
                {map !== null ? children : null}
            </GoogleMapsContext.Provider>
        </div>
    )
}

GoogleMaps.propTypes = {
    id: PropTypes.string.isRequired,
    isApiLoaded: PropTypes.bool,
    children: PropTypes.node,
    mapContainerStyle: PropTypes.object,
    mapContainerClassName: PropTypes.string,
    options: PropTypes.object,
    center: PropTypes.object,
    zoom: PropTypes.number,
    onLoad: PropTypes.func,
    onUnmount: PropTypes.func,
    onClick: PropTypes.func,
    onDblClick: PropTypes.func,
    onDrag: PropTypes.func,
    onDragEnd: PropTypes.func,
    onDragStart: PropTypes.func,
    onMouseMove: PropTypes.func,
    onMouseOut: PropTypes.func,
    onMouseOver: PropTypes.func,
    onMouseDown: PropTypes.func,
    onMouseUp: PropTypes.func,
    onRightClick: PropTypes.func,
    onCenterChanged: PropTypes.func
}

/**
 * Marker component for integrating Google Maps markers.
 *
 * This component creates and manages a Google Maps AdvancedMarkerElement on the provided map context.
 * It supports custom marker positions, icons, titles, and click event handling. The marker is automatically
 * cleaned up when the component is unmounted or when its position changes. The component also allows for
 * rendering children, which can be used for custom overlays or additional UI elements.
 *
 * Props:
 * @param {Object}   position   - The geographical position (LatLng or LatLngLiteral) where the marker should be placed.
 * @param {ReactNode} icon      - Optional custom icon or content for the marker.
 * @param {Function} onClick    - Optional callback function to be invoked when the marker is clicked.
 * @param {string}   title      - Optional title for the marker, shown as a tooltip.
 * @param {ReactNode} children  - Optional React children to render within the component.
 *
 * Context:
 * Utilizes GoogleMapsContext to access the map instance and set the currently active marker.
 *
 * Side Effects:
 * - Adds and removes the marker from the map as needed.
 * - Attaches click event listeners if an onClick handler is provided.
 */
export const Marker = ({position, icon, onClick, title, children}) => {
    const {map, setActiveMarker} = useContext(GoogleMapsContext)
    const markerRef = useRef(null)

    useEffect(() => {
        if (map) {
            // Clear existing marker
            if (markerRef.current) {
                markerRef.current.map = null
            }

            if (position && isGoogleMapsLibraryLoaded('marker')) {
                const markerOptions = {
                    map,
                    position,
                    ...(title && {title}),
                    ...(icon && {content: icon}),
                    ...(onClick && {gmpClickable: true})
                }

                const marker = new window.google.maps.marker.AdvancedMarkerElement(markerOptions)

                if (onClick) {
                    marker.addListener('gmp-click', () => {
                        onClick()
                        setActiveMarker(marker)
                    })
                }
                markerRef.current = marker
            }
        }

        return () => {
            if (markerRef.current) {
                markerRef.current.map = null
            }
        }
    }, [map, position])

    return <>{children}</>
}

Marker.propTypes = {
    position: PropTypes.shape({
        lat: PropTypes.number.isRequired,
        lng: PropTypes.number.isRequired
    }).isRequired,
    icon: PropTypes.any,
    onClick: PropTypes.func,
    title: PropTypes.string,
    children: PropTypes.node
}

/**
 * InfoWindow is a React component that renders a Google Maps InfoWindow.
 * It displays custom React children as the content of the InfoWindow and manages its lifecycle and positioning.
 *
 * @param {Object} props - The component props.
 * @param {Object} [props.position={lat: 0, lng: 0}] - The latitude and longitude where the InfoWindow should be anchored.
 * @param {Function} [props.onCloseClick] - Callback function invoked when the InfoWindow's close button is clicked.
 * @param {boolean} [props.stickToActiveMarker=true] - Determines whether the InfoWindow should be anchored to the currently active marker.
 * @param {React.ReactNode} props.children - The content to display inside the InfoWindow.
 *
 * @returns {React.ReactPortal} A React portal that renders the children into the InfoWindow's DOM container.
 *
 * @remarks
 * This component must be used within a GoogleMapsContext provider, which supplies the map and activeMarker objects.
 * It automatically cleans up the InfoWindow instance when the component unmounts or its dependencies change.
 *
 * The use of a React Portal allows to use React components and elements as the content of the InfoWindow,
 * providing a seamless integration between React and the Google Maps API.
 */
export const InfoWindow = ({
    position = {lat: 0, lng: 0},
    onCloseClick,
    stickToActiveMarker = true,
    children
}) => {
    const {map, activeMarker} = useContext(GoogleMapsContext)
    const infoWindowRef = useRef(null)
    const containerElement = useRef(document.createElement('div'))

    const handleCloseClick = useCallback(() => {
        if (onCloseClick) {
            onCloseClick()
        }
    }, [onCloseClick])

    useEffect(() => {
        if (map && position) {
            if (infoWindowRef.current) {
                infoWindowRef.current.close()
            }

            if (isGoogleMapsApiLoaded()) {
                const infoWindow = new window.google.maps.InfoWindow({
                    position
                })

                // children as InfoWindow content in containerElement.current portal
                infoWindow.setContent(containerElement.current)

                infoWindow.addListener('closeclick', handleCloseClick)

                infoWindow.open({
                    anchor: stickToActiveMarker ? activeMarker : undefined,
                    map
                })
                infoWindowRef.current = infoWindow
            }
        }

        return () => {
            if (infoWindowRef.current) {
                infoWindowRef.current.close()
            }
        }
    }, [map, position, children, handleCloseClick])

    return <Portal containerRef={containerElement}>{children}</Portal>
}

InfoWindow.propTypes = {
    position: PropTypes.shape({
        lat: PropTypes.number.isRequired,
        lng: PropTypes.number.isRequired
    }),
    onCloseClick: PropTypes.func,
    stickToActiveMarker: PropTypes.bool,
    children: PropTypes.node.isRequired
}

export default memo(GoogleMaps)
